/**
 * ─────────────────────────────────────────────
 * src/lib/googlePicker.js
 * ─────────────────────────────────────────────
 * Opens Google's own file picker and returns what the user chose.
 *
 * Google's scripts are loaded on demand rather than in index.html, so a
 * deployment without Drive credentials never fetches them at all, and the
 * login page is not slowed down by a feature used on one tab.
 *
 * The token requested is scoped to `drive.file` — it grants access only to
 * files the user explicitly picks, never their whole Drive. It is short-lived
 * and is handed to our own backend to download the file, so nothing about
 * their Drive is stored.
 */

const GSI_SRC = "https://accounts.google.com/gsi/client";
const GAPI_SRC = "https://apis.google.com/js/api.js";
// Only the files the user picks. Deliberately not drive.readonly.
const SCOPE = "https://www.googleapis.com/auth/drive.file";

// Cache the loads so opening the picker twice does not add the script twice.
const loaded = {};

const loadScript = (src) => {
  if (loaded[src]) return loaded[src];
  loaded[src] = new Promise((resolve, reject) => {
    // Another part of the app may already have added it.
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") return resolve();
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("load failed")));
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () => {
      // Let a later attempt retry rather than caching the failure.
      delete loaded[src];
      reject(new Error("Could not load Google Drive. Check your connection."));
    };
    document.body.appendChild(script);
  });
  return loaded[src];
};

const loadPickerApi = () =>
  new Promise((resolve, reject) => {
    if (window.google?.picker) return resolve();
    window.gapi.load("picker", {
      callback: resolve,
      onerror: () => reject(new Error("Could not load the Google picker.")),
    });
  });

/**
 * Asks Google for an access token, showing the consent screen if needed.
 */
const requestAccessToken = (clientId) =>
  new Promise((resolve, reject) => {
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: SCOPE,
      callback: (response) => {
        if (response.error) {
          return reject(
            new Error(
              response.error === "access_denied"
                ? "Google Drive access was declined."
                : "Could not sign in to Google Drive.",
            ),
          );
        }
        resolve(response.access_token);
      },
      error_callback: (err) => {
        reject(
          new Error(
            err?.type === "popup_closed"
              ? "The Google sign-in window was closed."
              : "Could not sign in to Google Drive.",
          ),
        );
      },
    });
    client.requestAccessToken();
  });

/**
 * Opens the picker and resolves with { fileId, name, mimeType, token }, or
 * null if the user cancelled.
 *
 * @param {object} config  { clientId, apiKey, appId } from the server
 */
export const pickFromGoogleDrive = async ({ clientId, apiKey, appId }) => {
  if (!clientId || !apiKey) {
    throw new Error("Google Drive is not configured on this server.");
  }

  await Promise.all([loadScript(GSI_SRC), loadScript(GAPI_SRC)]);
  await loadPickerApi();

  const token = await requestAccessToken(clientId);

  return new Promise((resolve, reject) => {
    try {
      // Spreadsheets, PDFs, CSVs and Google Sheets/Docs — the formats the
      // sprint importer can actually read.
      const view = new window.google.picker.DocsView(
        window.google.picker.ViewId.DOCS,
      )
        // Folders are shown so the user can navigate into them, but cannot
        // be chosen — picking one would send a folder id to the backend,
        // which has no file to download.
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false)
        .setMimeTypes(
          [
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.ms-excel",
            "application/vnd.google-apps.spreadsheet",
            "application/vnd.google-apps.document",
            "application/pdf",
            "text/csv",
            "text/plain",
          ].join(","),
        );

      const builder = new window.google.picker.PickerBuilder()
        .addView(view)
        .setOAuthToken(token)
        .setDeveloperKey(apiKey)
        // The callback payload is keyed by plain strings: data.action is
        // "picked" or "cancel", and data.docs holds the chosen files.
        //
        // Read literally rather than through google.picker.Action /
        // Response / Document: those enums do not all carry the members you
        // would expect (there is no Action.ACTION — the action key lives on
        // Response), and getting one wrong fails silently, leaving the
        // promise unresolved and the picker looking like it did nothing.
        .setCallback((data) => {
          const action = data?.action;
          if (action === "picked") {
            const doc = data?.docs?.[0];
            if (!doc) return resolve(null);
            resolve({
              fileId: doc.id,
              name: doc.name,
              mimeType: doc.mimeType,
              token,
            });
          } else if (action === "cancel") {
            resolve(null);
          }
          // Any other action ("loaded", for one) is progress, not a result.
        });

      if (appId) builder.setAppId(appId);
      const picker = builder.build();

      // If the user dismisses the picker with Escape or by clicking away,
      // Google does not always fire the "cancel" callback. Without this the
      // promise would never settle and the button would sit on "Opening…"
      // forever, which is indistinguishable from a hang.
      const onVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          setTimeout(() => {
            const dialog = document.querySelector(".picker-dialog");
            if (!dialog) {
              document.removeEventListener(
                "visibilitychange",
                onVisibilityChange,
              );
              resolve(null);
            }
          }, 400);
        }
      };
      document.addEventListener("visibilitychange", onVisibilityChange);

      picker.setVisible(true);
    } catch (err) {
      reject(
        new Error(
          `Could not open Google Drive${err?.message ? `: ${err.message}` : "."}`,
        ),
      );
    }
  });
};

export default { pickFromGoogleDrive };
